import { Box, Button, Menu, MenuItem } from '@mui/material'
import { createContext, forwardRef, ReactNode, useContext, useState } from 'react'

/** STYLE */
import { styled } from '@mui/material/styles'
import styleds from 'styled-components'

/** CONSTANTS */
import { colors } from 'constants/theme'

export const ResponsiveReportTable = styled(Box)<{ verticalAlign?: 'top' | 'middle' }>(({ theme, verticalAlign }) => ({
  '.MuiTable-root': {
    '.MuiTableHead-root': {
      '.MuiTableCell-head': {
        padding: '15px',
      }
    },
    '.MuiTableBody-root': {
      '.row-summary': {
        '.MuiTableCell-root': {
          backgroundColor: `${colors.lightGray} !important`,
          verticalAlign: 'middle !important',
          paddingTop: 10,
          paddingBottom: 10
        }
      },
      '.MuiTableCell-root': {
        paddingTop: verticalAlign === 'top' ? 16 : 10,
        paddingBottom: verticalAlign === 'top' ? 8 : 10,
        verticalAlign: `${verticalAlign} !important` || 'middle'
      }
    }
  }
}))

/** ปิดเมนูเมื่อคลิก DropdownItem (เลียนแบบพฤติกรรม react-bootstrap Dropdown.Item) — context ทะลุ portal ของ MUI Menu ได้ */
const DropdownCloseContext = createContext<() => void>(() => undefined)

const DocumentToggle = styled(Button)(({ theme }) => ({
  backgroundColor: `${colors.white} !important`,
  color: `${colors.themeSecondColor} !important`,
  border: `1px solid ${colors.themeSecondColor} !important`,
  display: 'flex',
  alignItems: 'center',
  boxShadow: 'none !important',
  height: 44,
  minWidth: 'unset',
  padding: '0.375rem 0.75rem',
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: 1.5,
  textTransform: 'none',
  borderRadius: '0.25rem',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: `${colors.white} !important`,
    boxShadow: 'none !important'
  },
  '&::after': {
    content: '""',
    display: 'inline-block',
    marginLeft: '0.5rem',
    verticalAlign: '0.255em',
    borderTop: '0.3em solid',
    borderRight: '0.3em solid transparent',
    borderBottom: 0,
    borderLeft: '0.3em solid transparent',
    transition: 'transform .15s ease'
  },
  '&[aria-expanded="true"]::after': {
    transform: 'rotate(180deg)'
  }
}))

const DocumentMenu = styled(Menu)(({ theme }) => ({
  '.MuiPaper-root': {
    marginTop: 4,
    borderRadius: 4,
    background: colors.white,
    border: `1px solid ${colors.lightGray}`,
    boxShadow: '0 .5rem 1rem rgba(0,0,0,.15)'
  },
  '.MuiList-root': {
    padding: 0
  }
}))

type DropdownDocumentProps = {
  title?: ReactNode
  id?: string
  className?: string
  sx?: any
  children?: ReactNode
}

export const DropdownDocument = ({ title, id, className, sx, children }: DropdownDocumentProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClose = () => setAnchorEl(null)
  return (
    <Box className={className} sx={sx}>
      <DropdownCloseContext.Provider value={handleClose}>
        <DocumentToggle id={id} aria-expanded={open} disableRipple onClick={(e) => setAnchorEl(e.currentTarget)}>
          {title}
        </DocumentToggle>
        <DocumentMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          disableAutoFocusItem
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {children}
        </DocumentMenu>
      </DropdownCloseContext.Provider>
    </Box>
  )
}

const DropdownMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: '1rem',
  '&:hover': {
    backgroundColor: colors.themeSecondColor06,
    color: colors.themeSecondColor
  }
}))

export const DropdownItem = forwardRef<HTMLLIElement, any>(({ onClick, children, ...rest }, ref) => {
  const close = useContext(DropdownCloseContext)
  return (
    <DropdownMenuItem
      ref={ref}
      onClick={(e: any) => {
        onClick?.(e)
        close()
      }}
      {...rest}
    >
      {children}
    </DropdownMenuItem>
  )
})
DropdownItem.displayName = 'DropdownItem'

export const Styles = styleds.div`
  padding: 1px;

  table {
    border-spacing: 0;
    th,
    td {
      margin: 0;
      padding: 1px;
      border-bottom: 1px solid black;
      border-top: 1px solid black;
      text-align: center;
      :last-child {
        border-right: 0;
      }
    }
  } 
`

export const StylesBorder = styleds.div`
  padding: 3px;

  table, th, td {
    padding: 1px;
    border: 1px solid black;
    border-collapse: collapse;
  }

  td {
    padding: 3px 5px;
  }
`

export const StylesNoneBorder = styled(Box)(({ theme }) => ({
  padding: '3px',
  'table, th, td': {
    padding: '1px',
    border: '1px solid transparent',
    borderCollapse: ' collapse',
  },
  td: {
    padding: '3px 5px',
  }
}))

export const StylesRemark = styleds.div`
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* number of lines to show */
            line-clamp: 2;
    -webkit-box-orient: vertical;
`


export const HeightAutocompleteSelect = styled('div')(({ theme }) => ({

  maxHeight: 32,
  position: 'relative',
  width: '100%',
  '.MuiOutlinedInput-root': {
    height: `32px !important`,
    padding: '0 !important',
    borderRadius: '4px 4px 4px 4px'
  },
  '.MuiOutlinedInput-input': {
    paddingLeft: '1rem !important',
    paddingRight: '2.25rem !important'
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderRadius: '0 4px 4px 0'
  },
}))

export const SpanClearFilter = styled('span')(({ theme }) => ({
  cursor: 'pointer',
  color: '#1cb99a !important',
  boxShadow: 'none !important'
}))

export const SummaryBox = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  backgroundColor: colors.lightGray,
  borderRadius: 4,
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px 24px',
  fontSize: 14,
  '& .me-3': {
    marginRight: 16
  }
}))